import { createAuthenticatedAxiosInstance, deleteWithAuthentication } from './api'
import { BASE_URL, LIBRARY_RESOURCE_ENDPOINT, GENERATE_RESOURCES_ENDPOINT } from '@/assets/constants/api_endpoints'
import {
  RESOURCE_TYPE_PDF,
  RESOURCE_TYPE_IMG,
  RESOURCE_TYPE_DOCX,
  RESOURCE_TYPE_TXT,
  RESOURCE_TYPE_URL
} from '@/assets/constants/constants'

const EXTENSION_MAP = {
  pdf: RESOURCE_TYPE_PDF,
  docx: RESOURCE_TYPE_DOCX,
  doc: RESOURCE_TYPE_DOCX,
  txt: RESOURCE_TYPE_TXT,
  jpg: RESOURCE_TYPE_IMG,
  jpeg: RESOURCE_TYPE_IMG,
  png: RESOURCE_TYPE_IMG,
  gif: RESOURCE_TYPE_IMG,
  webp: RESOURCE_TYPE_IMG
}

/**
 * Infers the resource_type from the resource title (filename or URL).
 *
 * @param {string} title - The resource title, e.g. "document.docx" or "https://..."
 * @returns {string} One of the RESOURCE_TYPE_* constant values
 */
export function inferResourceType(title) {
  if (!title) return RESOURCE_TYPE_TXT
  if (title.startsWith('http://') || title.startsWith('https://')) return RESOURCE_TYPE_URL
  const ext = title.split('.').pop().toLowerCase()
  return EXTENSION_MAP[ext] || RESOURCE_TYPE_TXT
}

/**
 * Deletes a resource from the library.
 *
 * Sends DELETE to the resources endpoint with body { resource_id: string }.
 *
 * @param {string|number} resourceId - The ID of the resource to delete
 * @returns {Promise<void>}
 */
export async function deleteResource(resourceId) {
  await deleteWithAuthentication(BASE_URL, LIBRARY_RESOURCE_ENDPOINT, {
    resource_id: String(resourceId)
  })
}

/**
 * Saves a URL resource to the library under a given subject.
 *
 * @param {string} url - The URL to save
 * @param {string|number} subjectId - The subject to save under
 * @returns {Promise<Object>} The created resource
 */
export async function saveUrlResource(url, subjectId) {
  const formData = new FormData()
  formData.append('subject_id', subjectId)
  formData.append('resource_type', RESOURCE_TYPE_URL)
  formData.append('resource_url', url)
  formData.append('file_details', url)

  const axiosInstance = createAuthenticatedAxiosInstance()
  const response = await axiosInstance.post(BASE_URL + LIBRARY_RESOURCE_ENDPOINT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

/**
 * Sends a request to generate AI documents for a given subject.
 *
 * @param {Object} payload
 * @param {number|string} payload.subject_id
 * @param {string} payload.topic
 * @param {string} payload.language
 * @param {string} payload.linguistic_level
 * @param {Array<{document_type: string, keywords: string[], other_considerations: string}>} payload.documents
 * @returns {Promise<Object>} API response data
 */
export async function generateResources(payload) {
  const axiosInstance = createAuthenticatedAxiosInstance()
  const response = await axiosInstance.post(BASE_URL + GENERATE_RESOURCES_ENDPOINT, payload)
  return response
}
